/**
 * Escaping for the FAQ section template engine.
 *
 * Every string a template interpolates is either escaped here or is answer
 * HTML the server already sanitised (`Article::sanitizePublicContent`), which
 * the engine places itself after the template has rendered — see
 * `renderFaqSection()`. Templates never see raw customer text.
 */

/**
 * @param {unknown} value
 * @returns {string}
 */
export function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

const NAMED_ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };

/**
 * The words of an answer, for the search templates' `data-text` attribute.
 *
 * Stripping tags is not enough on its own: a text node may hold a raw `"`,
 * which would close the attribute and let the rest of the answer write
 * attributes of its own. So the text is decoded to plain characters first and
 * the caller escapes it once, like every other interpolated string. Works
 * without a DOM, because the engine also runs in Node (tests/js).
 *
 * @param {string} html
 * @returns {string}
 */
export function htmlToText(html) {
    return String(html ?? '')
        .replace(/<(script|style|template)\b[\s\S]*?<\/\1>/gi, ' ')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (entity, body) => {
            if (body[0] === '#') {
                const code = body[1] === 'x' || body[1] === 'X'
                    ? parseInt(body.slice(2), 16)
                    : parseInt(body.slice(1), 10);
                return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : ' ';
            }
            return NAMED_ENTITIES[body.toLowerCase()] ?? entity;
        })
        .replace(/\s+/g, ' ')
        .trim();
}
